import classNames from 'classnames';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import styles from './departure-selector-item.module.scss';

export interface DepartureSelectorItemProps {
  /** The class name to apply */
  className?: string;
  /** The departure item data */
  departureItem: Content.Components['DepartureSelectorModuleDepartureDto'];
  /** Indicates whether the option is selected */
  isSelected?: boolean;
  /** An event that is triggered on click */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** The price label to show */
  priceLabel?: string;
}

const DepartureSelectorItem = ({
  className,
  departureItem,
  isSelected = false,
  onClick,
  priceLabel,
  ...rest
}: DepartureSelectorItemProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();
  const { dateFormatter } = useDateFormatter();

  return (
    <button
      {...rest}
      className={classNames(styles.component, className, {
        [styles.isSelected]: isSelected,
      })}
      onClick={onClick}
      tabIndex={0}
    >
      <div className={styles.mobileContent}>
        <div className={styles.priceWrapper}>
          <div className={styles.priceMobile}>
            <Typography tagStyle="bodySmall">{priceLabel}</Typography>

            <Typography tagStyle="priceSmall">
              {currencyFormatter(departureItem.price)}
            </Typography>
          </div>

          <div className={styles.selectedIconWrapper}>
            {isSelected && <Icon name="CheckMark" size={24} />}
          </div>
        </div>

        <div className={styles.bottomRow}>
          <div className={styles.dateWrapper}>
            <Typography tagStyle="bodySmall">
              {t('items.modules.departureSelector.date')}
            </Typography>

            <div className={styles.dateMobile}>
              <Typography tagStyle="bodyMediumBold">
                {`${dateFormatter(departureItem.outboundDepartureDate).format('DD. MMM')} - ${dateFormatter(departureItem.homeboundDepartureDate).format('DD. MMM YYYY')}`}
              </Typography>

              <Typography tagStyle="bodySmall">
                {departureItem.duration} {t('items.common.words.days')}
              </Typography>
            </div>
          </div>

          <div className={styles.airportMobile}>
            <Typography tagStyle="bodySmall">
              {t('items.modules.departureSelector.airport')}
            </Typography>

            <Typography tagStyle="bodyMediumBold">
              {`${departureItem.outboundDepartureName} (${departureItem.outboundDepartureCode})`}
            </Typography>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.date}>
          <Typography tagStyle="bodyLargeBold">
            {`${dateFormatter(departureItem.outboundDepartureDate).format('DD. MMM')} - ${dateFormatter(departureItem.homeboundDepartureDate).format('DD. MMM YYYY')}`}
          </Typography>

          <Typography tagStyle="bodyMedium">
            {departureItem.duration} {t('items.common.words.days')}
          </Typography>
        </div>

        <div className={styles.airport}>
          <Typography tagStyle="bodyLargeBold">
            {departureItem.outboundDepartureName}
          </Typography>

          <Typography tagStyle="bodyMedium">
            ({departureItem.outboundDepartureCode})
          </Typography>
        </div>

        <div className={styles.price}>
          <Typography tagStyle="bodyMedium">{priceLabel}</Typography>

          <Typography tagStyle="bodyLargeBold">
            {currencyFormatter(departureItem.price)}
          </Typography>
        </div>

        <div className={styles.selectedIconWrapper}>
          {isSelected && <Icon name="CheckMark" size={24} />}
        </div>
      </div>
    </button>
  );
};

DepartureSelectorItem.displayName = 'DepartureSelectorItem';

export default DepartureSelectorItem;
